import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { memberService } from '../../services/memberService';
import { loanService } from '../../services/loanService';
import { savingsService } from '../../services/savingsService';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/formatters';
import toast from 'react-hot-toast';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  IdentificationIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  PencilIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const MemberDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch member details
  const { data: memberData, isLoading: memberLoading, refetch } = useQuery(
    ['member', id],
    () => memberService.getMemberById(id),
    { enabled: !!id }
  );

  // Fetch member savings
  const { data: savingsData } = useQuery(
    ['member-savings', id],
    () => savingsService.getMemberSavings(id),
    { enabled: !!id }
  );

  // Fetch member loans
  const { data: loansData } = useQuery(
    ['member-loans', id],
    () => loanService.getLoans({ member_id: id }),
    { enabled: !!id }
  );

  // Fetch member dashboard data
  const { data: dashboardData } = useQuery(
    ['member-dashboard', id],
    () => memberService.getMemberDashboard(id),
    { enabled: !!id }
  );

  // Transfer member mutation
  const transferMutation = useMutation(
    (transferData) => memberService.transferMember(id, transferData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['member', id]);
        toast.success('Member transferred successfully');
        setShowTransferModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Transfer failed');
      }
    }
  );

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    to_branch_id: '',
    to_samity_id: '',
    transfer_reason: '',
    remarks: ''
  });

  if (memberLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const member = memberData?.data;
  const savings = savingsData?.data || [];
  const loans = loansData?.data || [];
  const dashboard = dashboardData?.data || {};

  if (!member) {
    return (
      <div className="p-6 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800">Member Not Found</h3>
          <p className="text-red-600 mt-2">The member you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/members')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Members
          </button>
        </div>
      </div>
    );
  }

  const totalSavings = savings.reduce((sum, acc) => sum + (acc.current_balance || 0), 0);
  const totalLoans = loans.reduce((sum, loan) => sum + (loan.principal_amount || 0), 0);
  const outstandingLoans = loans.reduce((sum, loan) => {
    if (loan.status === 'active' || loan.status === 'disbursed') {
      return sum + (loan.outstanding_balance || loan.principal_amount || 0);
    }
    return sum;
  }, 0);

  return (
    <div className="p-6">
      {/* Header with Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/members')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Members
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{member.name}</h1>
            <p className="text-gray-600">Member Code: {member.member_code}</p>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/members/${id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Member
            </Link>
            <button
              onClick={() => setShowTransferModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              disabled={member.status !== 'active'}
            >
              Transfer Member
            </button>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`mb-6 p-4 rounded-lg ${
        member.status === 'active' ? 'bg-green-50 border border-green-200' :
        member.status === 'pending_approval' ? 'bg-yellow-50 border border-yellow-200' :
        member.status === 'inactive' ? 'bg-gray-50 border border-gray-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 ${
            member.status === 'active' ? 'bg-green-500' :
            member.status === 'pending_approval' ? 'bg-yellow-500' :
            'bg-gray-500'
          }`}></div>
          <span className="font-medium">Status: {member.status.toUpperCase()}</span>
          {member.status === 'pending_approval' && (
            <button
              onClick={() => memberService.approveMember(id).then(() => refetch())}
              className="ml-4 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
            >
              Approve Member
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-4 text-white">
          <div className="text-sm opacity-90">Total Savings</div>
          <div className="text-2xl font-bold">{formatCurrency(totalSavings)}</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-4 text-white">
          <div className="text-sm opacity-90">Total Loans</div>
          <div className="text-2xl font-bold">{formatCurrency(totalLoans)}</div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow p-4 text-white">
          <div className="text-sm opacity-90">Outstanding Loans</div>
          <div className="text-2xl font-bold">{formatCurrency(outstandingLoans)}</div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow p-4 text-white">
          <div className="text-sm opacity-90">Membership Days</div>
          <div className="text-2xl font-bold">{dashboard.membership_days || 0}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-4">
          {['overview', 'savings', 'loans', 'transactions', 'documents'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Full Name</div>
                <div className="font-medium">{member.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Father's Name</div>
                <div className="font-medium">{member.father_name || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Mother's Name</div>
                <div className="font-medium">{member.mother_name || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Spouse Name</div>
                <div className="font-medium">{member.spouse_name || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Date of Birth</div>
                <div className="font-medium">{member.date_of_birth ? formatDate(member.date_of_birth) : 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Gender</div>
                <div className="font-medium capitalize">{member.gender || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">NID Number</div>
                <div className="font-medium">
                  {member.nid || 'N/A'}
                  {member.nid_verified && (
                    <span className="ml-2 text-xs text-green-600">✓ Verified</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Occupation</div>
                <div className="font-medium">{member.occupation || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Monthly Income</div>
                <div className="font-medium">{member.monthly_income ? formatCurrency(member.monthly_income) : 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Joining Date</div>
                <div className="font-medium">{formatDate(member.joining_date)}</div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <PhoneIcon className="h-5 w-5 mr-2 text-gray-500" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Phone Number</div>
                <div className="font-medium">{member.phone}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Alternate Phone</div>
                <div className="font-medium">{member.alternate_phone || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Email</div>
                <div className="font-medium">{member.email || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Emergency Contact</div>
                <div className="font-medium">
                  {member.emergency_contact_name ? `${member.emergency_contact_name} (${member.emergency_contact})` : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-gray-500" />
              Address Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Present Address</div>
                <div className="font-medium">{member.present_address || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Permanent Address</div>
                <div className="font-medium">{member.permanent_address || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Nominee Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <IdentificationIcon className="h-5 w-5 mr-2 text-gray-500" />
              Nominee Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Nominee Name</div>
                <div className="font-medium">{member.nominee_name || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Nominee Relation</div>
                <div className="font-medium">{member.nominee_relation || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Nominee Phone</div>
                <div className="font-medium">{member.nominee_phone || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Savings Tab */}
      {activeTab === 'savings' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Savings Accounts</h2>
            <Link
              to={`/savings/new?memberId=${member.id}`}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              + Open Account
            </Link>
          </div>

          {savings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BanknotesIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No savings accounts found</p>
              <Link
                to={`/savings/new?memberId=${member.id}`}
                className="inline-block mt-2 text-blue-600 hover:text-blue-800"
              >
                Open first account
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheme</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {savings.map((account) => (
                    <tr key={account.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {account.account_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.scheme_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        {formatCurrency(account.current_balance)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {account.interest_rate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(account.status)}`}>
                          {account.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/savings/${account.id}/transactions`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Transactions
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
               </table>
            </div>
          )}
        </div>
      )}

      {/* Loans Tab */}
      {activeTab === 'loans' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold">Loan History</h2>
            <Link
              to={`/loans/apply?memberId=${member.id}`}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
            >
              + Apply for Loan
            </Link>
          </div>

          {loans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CreditCardIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No loan history found</p>
              <Link
                to={`/loans/apply?memberId=${member.id}`}
                className="inline-block mt-2 text-blue-600 hover:text-blue-800"
              >
                Apply for first loan
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">EMI</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disbursement Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {loan.loan_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(loan.principal_amount)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(loan.emi_amount)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(loan.status)}`}>
                          {loan.status}
                        </span>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {loan.disbursement_date ? formatDate(loan.disbursement_date) : 'Not disbursed'}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link
                          to={`/loans/${loan.id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Details
                        </Link>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Recent Transactions</h2>
          </div>

          {dashboard.recent_transactions?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ArrowPathIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(dashboard.recent_transactions || []).filter(transaction => transaction.status === 'active' || transaction.type === null).map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transaction.date)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          transaction.type === 'savings_deposit' ? 'bg-green-100 text-green-800' :
                          transaction.type === 'loan_repayment' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.type?.replace('_', ' ')}
                        </span>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {formatCurrency(transaction.amount)}
                       </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {transaction.transaction_type || '-'}
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-500" />
            Documents
          </h2>
          <div className="text-center py-12 text-gray-500">
            <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p>Document management coming soon</p>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Transfer Member</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              transferMutation.mutate(transferData);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Transfer To Branch</label>
                  <input
                    type="number"
                    value={transferData.to_branch_id}
                    onChange={(e) => setTransferData({ ...transferData, to_branch_id: e.target.value })}
                    placeholder="Branch ID"
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Transfer To Samity</label>
                  <input
                    type="number"
                    value={transferData.to_samity_id}
                    onChange={(e) => setTransferData({ ...transferData, to_samity_id: e.target.value })}
                    placeholder="Samity ID (optional)"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Transfer Reason</label>
                  <textarea
                    value={transferData.transfer_reason}
                    onChange={(e) => setTransferData({ ...transferData, transfer_reason: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Remarks</label>
                  <textarea
                    value={transferData.remarks}
                    onChange={(e) => setTransferData({ ...transferData, remarks: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferMutation.isLoading}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {transferMutation.isLoading ? 'Processing...' : 'Transfer Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetails;